import React, { useState, useEffect } from "react";
import { PodMetric } from "../../store/slices/podMetricsSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  CheckmarkCircle16Filled,
  Flag16Filled,
  Flag16Regular,
  WrenchFilled,
} from "@fluentui/react-icons";
import { getStatusBadgeText, statusTextColorMap } from "../../types/types";
import { Info16Regular } from "@fluentui/react-icons";
import { Warning16Filled } from "@fluentui/react-icons";
import { ErrorCircle16Filled } from "@fluentui/react-icons";

import { Tooltip } from "@material-tailwind/react";

interface PodServiceCardProps {
  containerName: string;
  pods: PodMetric[];
  lag?: number | string | any;
  isCritical?: boolean;
}

// Define valid status types to match the keys in statusTextColorMap
type StatusType =
  | "Failed"
  | "Pending"
  | "Running"
  | "Maintenance"
  | "Outage"
  | "Degraded"
  | "Operational";

const PodServiceCard: React.FC<PodServiceCardProps> = ({
  containerName,
  pods,
  lag = 0,
  isCritical = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInMaintenance, setIsInMaintenance] = useState(false);
  
  // Get maintenance status from Redux store
  const { globalMaintenance, activeSessions, serviceMaintenance } = useSelector(
    (state: RootState) => state.maintenance
  );
  
  const { criticalServices, cluster: currentCluster } = useSelector(
    (state: RootState) => state.criticalServices
  );

  // Helper function to check if maintenance is currently active based on time range
  const isMaintenanceActive = (session: any) => {
    if (!session || !session.start_time || !session.end_time) return false;
    
    const now = new Date().getTime();
    const start = new Date(session.start_time).getTime();
    const end = new Date(session.end_time).getTime();
    
    return now >= start && now <= end;
  };

  // Check if this service is in maintenance mode
  // Use local state to override the isCritical prop with data from Redux
  const [isServiceCritical, setIsServiceCritical] = useState(isCritical);
  
  // Check if this service is marked as critical in Redux store
  useEffect(() => {
    const serviceName = pods[0]?.container_name || containerName;
    
    // Always reset critical status first - important when switching clusters
    setIsServiceCritical(false);
    
    // Update critical status based on Redux state
    if (criticalServices && criticalServices.length > 0) {
      const criticalFromRedux = criticalServices.some(
        service => service.service_name === serviceName && service.enabled
      );
      
      if (criticalFromRedux) {
        setIsServiceCritical(true);
      }
    }
  }, [criticalServices, pods, containerName, currentCluster]);

  useEffect(() => {
    // Check global maintenance first
    if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) {
      setIsInMaintenance(true);
      return;
    } 
    // Check service-specific maintenance
    if (activeSessions && activeSessions.length > 0) {
      // Get the container name from the first pod - THIS IS THE KEY PART
      const containerNameToCheck = pods[0]?.container_name || containerName;
      // DIRECT CHECK: See if the service is in maintenance
      if (serviceMaintenance && serviceMaintenance[containerNameToCheck]) {
        // Check if the maintenance is currently active based on time range
        if (isMaintenanceActive(serviceMaintenance[containerNameToCheck])) {
          setIsInMaintenance(true);
          return;
        }
      }
      let inMaintenance = false;
      
      // Method 1: Direct lookup in serviceMaintenance object
      if (serviceMaintenance && serviceMaintenance[containerNameToCheck] && 
          isMaintenanceActive(serviceMaintenance[containerNameToCheck])) {
            inMaintenance = true;
      }
      setIsInMaintenance(inMaintenance);
    } else {
      setIsInMaintenance(false);
    }
  }, [globalMaintenance, activeSessions, containerName, pods]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  // sum of lag
  const lagSummary =
    lag.length > 0
      ? lag.reduce((acc: number, curr: any) => acc + curr.lag, 0)
      : "-";

  // Determine the overall status of the container based on pod statuses, lag, and maintenance status
  const getContainerStatus = (): StatusType => {
    // First check if global maintenance is active and within time range
    if (globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) return "Maintenance";
    
    // Then check if the service is in maintenance mode from the maintenance API
    if (isInMaintenance) return "Maintenance";
    
    // Then check if any pods are marked as maintenance in pod metrics
    if (pods.some((pod) => pod.phase === "Maintenance")) return "Maintenance";
    
    // If there's significant lag, show as Failed regardless of pod status
    if (typeof lagSummary === "number" && lagSummary > 10000) return "Failed";

    // Otherwise check pod statuses
    if (pods.some((pod) => pod.phase === "Failed")) return "Failed";
    if (pods.some((pod) => pod.phase === "Pending")) return "Pending";

    // Even with small lag, show as degraded
    if (typeof lagSummary === "number" && lagSummary > 1000) return "Pending";

    return "Running";
  };

  // Count running pods
  const runningPods = pods.filter((pod) => pod.phase === "Running").length;
  const containerStatus = getContainerStatus();

  // Get all event reasons from pods
  const eventReasons = pods
    .filter((pod) => pod.event_reason)
    .map((pod) => `${pod.pod_name}: ${pod.event_reason}`)
    .join("\n");

  const lagReason =
    lagSummary > 10000
      ? "Extremealy High Lag"
      : lagSummary > 1000
        ? "High Lag"
        : null;

  return (
    <div className="box-border flex flex-col items-start p-0 bg-white border border-gray-300 rounded-lg shadow-xl w-full overflow-hidden">
      <div
        onClick={toggleAccordion}
        className="px-4 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 w-full"
      >
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className={`w-4 h-4 mr-2 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
          {isServiceCritical ? (
            <div className="w-8">
              <Flag16Filled color="#e81818" />
            </div>
          ) : (
            <div className="w-8">
              <Flag16Regular />
            </div>
          )}
          <span className="font-inter font-medium text-base leading-6 text-gray-700">
            {containerName} {runningPods}/{pods.length}
            <Tooltip
              content={
                <div className="p-1 rounded-none w-96">
                  <div className="flex justify-between items-center">
                    <div className="font-inter font-semibold text-sm text-gray-50">
                      {containerName}
                    </div>
                    {containerStatus === "Pending" && (
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500">
                          <Warning16Filled />
                        </span>
                        <span className="text-yellow-500 font-inter font-semibold text-sm">
                          Degraded
                        </span>
                      </div>
                    )}
                    {containerStatus === "Failed" && (
                      <div className="flex items-center gap-1">
                        <span className="text-red-500">
                          <ErrorCircle16Filled />
                        </span>
                        <span className="text-red-500 font-inter font-semibold text-sm">
                          Failed
                        </span>
                      </div>
                    )}
                    {containerStatus === "Outage" && (
                      <div className="flex items-center gap-1">
                        <span className="text-red-500">
                          <ErrorCircle16Filled />
                        </span>
                        <span className="text-red-500 font-inter font-semibold text-sm">
                          Failed
                        </span>
                      </div>
                    )}
                    {containerStatus === "Degraded" && (
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500">
                          <Warning16Filled />
                        </span>
                        <span className="text-yellow-500 font-inter font-semibold text-sm">
                          Degraded
                        </span>
                      </div>
                    )}
                    {containerStatus === "Running" && (
                      <div className="flex items-center gap-1">
                        <span className="text-green-500">
                          <CheckmarkCircle16Filled />
                        </span>
                        <span className="text-green-500 font-inter font-semibold text-sm">
                          Operational
                        </span>
                      </div>
                    )}
                    {containerStatus === "Maintenance" && (
                      <div className="flex items-center gap-1">
                        <span className="text-blue-500">
                          <WrenchFilled />
                        </span>
                        <span className="text-blue-500 font-inter font-semibold text-sm">
                          Maintenance
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="font-inter font-medium text-xs tracking-tight text-gray-200 mt-1">
                    {pods[0].description}
                  </div>
                  {containerStatus !== "Running" && (
                    <>
                      <hr className="my-2 border-gray-700" />
                      <div className="flex items-center justify-between">
                        <div className="font-inter font-medium text-xs leading-4 text-center tracking-tight text-gray-200">
                          When system is{" "}
                          <span
                            className={`${statusTextColorMap[containerStatus]}`}
                          >
                            {containerStatus === "Failed" ? "Failed" : containerStatus === "Pending" ? "Degraded" : containerStatus === "Outage" ? "Outage" : containerStatus === "Maintenance" ? "Maintenance" : "Operational"}
                          </span>
                          , it means that the system is ...
                        </div>
                      </div>
                    </>
                  )}
                </div>
              }
              className="z-50"
              placement="top"
            >
              <span>
                <Info16Regular
                  className="w-4 h-4 cursor-pointer ml-2"
                  color="#667085"
                />
              </span>
            </Tooltip>
          </span>
        </div>
        <div className="flex items-center">
          <span
            className={`
              font-inter font-medium text-base
              ${statusTextColorMap[containerStatus]}
            `}
          >
            {getStatusBadgeText(containerStatus)}
          </span>
        </div>
      </div>
      {isOpen && (
        <>
          <div className="px-4 py-3 border-t border-gray-200 w-full">
            {pods.map((pod) => (
              <div
                key={`${pod.namespace}-${pod.pod_name}`}
                className="py-2 border-t border-gray-200 first:border-0"
              >
                <div className="flex items-center justify-between">
                  <span className="font-inter font-medium text-base text-gray-700">
                    {pod.pod_name}
                  </span>
                  <span
                    className={`
                    font-inter font-medium text-base
                    ${(globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) || isInMaintenance ? statusTextColorMap["Maintenance"] : statusTextColorMap[pod.phase as StatusType] || "text-green-500"}
                  `}
                  >
                    {(globalMaintenance?.enabled && isMaintenanceActive(globalMaintenance)) || isInMaintenance ? "Maintenance" : getStatusBadgeText(pod.phase)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Namespace: {pod.namespace} | Node: {pod.node_name} | Restarts:{" "}
                  {pod.restart_count} | Lag:{" "}
                  {Array.isArray(lag)
                    ? (() => {
                        // Get pod index to match with partition
                        const podIndex =
                          pods.findIndex((p) => p.pod_name === pod.pod_name) %
                          (lag.length || 1);
                        // Find the matching partition object
                        const partitionData = lag.find(
                          (item) =>
                            item.partition &&
                            item.partition.partition === podIndex
                        );
                        return partitionData ? partitionData.lag : "-";
                      })()
                    : "-"}
                </div>
              </div>
            ))}
          </div>
          {eventReasons ||
            (lagReason && (
              <div className="px-4 py-3 bg-gray-100 w-full border-t border-gray-200">
                <div className="flex items-center justify-center">
                  <span className="font-inter font-medium text-sm text-gray-500">
                    Event Reasons: {eventReasons || lagReason}
                  </span>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default PodServiceCard;
